/**
 * Qué palabras cambian entre los dos enunciados de un par de «Duplicados».
 *
 * Por qué existe: la sección le pone a una persona dos párrafos largos en texto plano y le pide que
 * decida si dicen lo mismo. En producción (16/09/2026) el par más frecuente difería en DOS palabras
 * sobre ciento treinta caracteres — «al bajar el pedido» contra «al momento de bajar el pedido» —
 * y encontrarlas era trabajo del lector. De las propuestas abiertas de «Dicen lo mismo», trece
 * difieren en seis palabras o menos.
 *
 * Es LCS sobre palabras, no sobre caracteres: marcar letras sueltas dentro de una palabra se lee
 * peor que marcar la palabra entera, y además parte los acentos.
 *
 * La comparación va sobre una clave NORMALIZADA (sin tildes, sin puntuación, en minúsculas) y lo
 * que se pinta es el texto original: sin eso, «pedido.» y «pedido» salen marcados como distintos
 * por el punto final, que es ruido puro.
 */

// LCS es O(n·m). Dos enunciados de la Memoria son decenas de palabras, pero el tope evita que un
// hecho anómalo (un documento entero pegado como un solo hecho) cuelgue el navegador. Por encima
// del tope no se marca nada y los dos textos salen enteros, que es exactamente lo de antes.
const MAX_WORDS = 400;

const SEPARATOR = /(\s+)/;

export const normalizeWord = (word) => word
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]/gu, '');

/** El texto partido en palabras y espacios, cada palabra con su clave de comparación. */
const tokenize = (text) => (text || '')
    .split(SEPARATOR)
    .filter((piece) => piece !== '')
    .map((piece) => ({raw: piece, key: /\s/.test(piece) ? null : normalizeWord(piece)}));

/** Los índices de las palabras COMUNES, en orden (subsecuencia común más larga). */
const commonWordIndexes = (wordsA, wordsB) => {
    const rows = wordsA.length;
    const cols = wordsB.length;
    const table = Array.from({length: rows + 1}, () => new Uint16Array(cols + 1));
    for (let i = rows - 1; i >= 0; i -= 1) {
        for (let j = cols - 1; j >= 0; j -= 1) {
            table[i][j] = wordsA[i].key === wordsB[j].key
                ? table[i + 1][j + 1] + 1
                : Math.max(table[i + 1][j], table[i][j + 1]);
        }
    }
    const inA = new Set();
    const inB = new Set();
    let i = 0;
    let j = 0;
    while (i < rows && j < cols) {
        if (wordsA[i].key === wordsB[j].key) {
            inA.add(wordsA[i].index);
            inB.add(wordsB[j].index);
            i += 1;
            j += 1;
        } else if (table[i + 1][j] >= table[i][j + 1]) {
            i += 1;
        } else {
            j += 1;
        }
    }
    return {inA, inB};
};

/** Los tokens de un lado agrupados en tramos seguidos de igual estado, para no emitir un `<span>`
 *  por palabra. */
const toSegments = (tokens, commonIndexes, everythingIsCommon) => tokens.reduce((segments, token) => {
    const changed = !everythingIsCommon && token.key !== null && !commonIndexes.has(token.index);
    const last = segments[segments.length - 1];
    if (last && last.changed === changed) {
        last.text += token.raw;
        return segments;
    }
    segments.push({text: token.raw, changed});
    return segments;
}, []);

/**
 * `diffStatements(a, b)` → `{a: [{text, changed}], b: [{text, changed}]}`.
 *
 * `changed` marca lo que está en ese lado y no en el otro. Los espacios nunca se marcan: pintar el
 * espacio de al lado agranda el resaltado sin agregar información.
 */
export const diffStatements = (statementA, statementB) => {
    const withIndexA = tokenize(statementA).map((token, index) => ({...token, index}));
    const withIndexB = tokenize(statementB).map((token, index) => ({...token, index}));
    const wordsA = withIndexA.filter((token) => token.key !== null);
    const wordsB = withIndexB.filter((token) => token.key !== null);

    const tooLong = wordsA.length > MAX_WORDS || wordsB.length > MAX_WORDS;
    const {inA, inB} = tooLong ? {inA: new Set(), inB: new Set()} : commonWordIndexes(wordsA, wordsB);

    return {
        a: toSegments(withIndexA, inA, tooLong),
        b: toSegments(withIndexB, inB, tooLong),
    };
};
